<template>
  <div class="chef-screen min-h-screen bg-slate-100 text-slate-900">
    <header class="chef-header">
      <div class="chef-header-main">
        <div class="chef-identity">
          <h1>{{ selectedKitchenName || '厨师任务大屏' }}</h1>
          <span class="chef-identity-divider" aria-hidden="true"></span>
          <strong>{{ selectedStoveName || chefName }}</strong>
          <span class="chef-shift-status" :class="{ 'is-enabled': canOperateCurrentKitchen }">
            {{ canControlShift ? (shiftEnabled ? '接单中' : '已停止接单') : '仅查看' }}
          </span>
        </div>
        <div class="chef-header-actions">
          <button
            v-if="canControlShift"
            type="button"
            class="chef-header-button chef-shift-button"
            :disabled="loading || scheduleSaving || stoveBusy || busyTaskId !== null || masterBusy"
            @click="toggleShiftStatus"
          >
            {{ stoveBusy ? '处理中…' : shiftEnabled ? '下班停止' : '上班启用' }}
          </button>
          <button
            type="button"
            class="chef-header-button"
            :disabled="
              loading || scheduleSaving || manualRefreshing || stoveBusy || busyTaskId !== null
            "
            @click="refreshManually"
          >
            <i
              class="fas fa-sync-alt"
              :class="{ 'fa-spin': loading || manualRefreshing }"
              aria-hidden="true"
            ></i>
            {{ loading || manualRefreshing ? '刷新中' : '刷新' }}
          </button>
          <button
            type="button"
            class="chef-header-button"
            :aria-expanded="showSettings || !currentKitchen"
            aria-controls="chef-settings"
            @click="showSettings = !showSettings"
          >
            <i class="fas fa-sliders-h" aria-hidden="true"></i>设置
          </button>
          <router-link to="/" class="chef-home-link" aria-label="返回首页">
            <i class="fas fa-home" aria-hidden="true"></i>
          </router-link>
        </div>
      </div>
      <div class="chef-system-status">
        <span
          ><i class="fas fa-play-circle" aria-hidden="true"></i
          >{{ canOperateCurrentKitchen ? '空闲时自动开始下一道菜' : '制作任务（仅查看）' }}</span
        >
        <span v-if="stoves.length" class="chef-master-status" :class="{ 'is-master': isMaster }">
          {{ isMaster ? '自动分配主屏' : '跟随主屏分配' }}
        </span>
        <span class="chef-refresh-note">每 15 秒刷新</span>
      </div>
      <div v-show="showSettings || !currentKitchen" id="chef-settings" class="chef-settings">
        <label>
          <span>餐厅</span>
          <select v-model="currentShop" disabled>
            <option v-for="shop in accessibleShops" :key="shop.shopId" :value="String(shop.shopId)">
              {{ shop.shopName }}
            </option>
          </select>
        </label>
        <label v-if="kitchens.length">
          <span>厨房</span>
          <select
            v-model="currentKitchen"
            :disabled="loading || scheduleSaving || stoveBusy || busyTaskId !== null || masterBusy"
          >
            <option v-for="kitchen in kitchens" :key="kitchen.id" :value="kitchen.id">
              {{ kitchen.name }}
            </option>
          </select>
        </label>
        <label v-if="ownStoves.length">
          <span>厨师工位</span>
          <select
            v-model="selectedStoveId"
            :disabled="loading || scheduleSaving || stoveBusy || busyTaskId !== null || masterBusy"
          >
            <option v-for="stove in ownStoves" :key="stove.stoveId" :value="String(stove.stoveId)">
              {{ stove.stoveName }}（{{ stove.stoveStatus === 0 ? '已启用' : '已停止' }}）
            </option>
          </select>
        </label>
        <label v-if="stoves.length" class="chef-master-switch">
          <input
            :checked="masterSelected"
            :disabled="scheduleSaving || stoveBusy || busyTaskId !== null || loading || masterBusy"
            type="checkbox"
            @change="toggleMaster"
          />
          <span>本页面为自动分配主屏</span>
        </label>
        <button
          v-if="stoves.length && !isMaster"
          type="button"
          class="chef-header-button"
          :disabled="scheduleSaving || stoveBusy || busyTaskId !== null || loading || masterBusy"
          @click="takeoverMaster"
        >
          强制接管主屏
        </button>
        <label>
          <span>预约提前制作</span>
          <input
            type="number"
            min="0"
            step="1"
            inputmode="numeric"
            aria-label="预约提前制作分钟数"
            :value="scheduleMinutesInput"
            :disabled="!canEditSchedule || scheduleSaving || stoveBusy || busyTaskId !== null"
            @input="editScheduleMinutes"
            @focus="scheduleInputFocused = true"
            @blur="finishScheduleInput"
            @keydown.enter.prevent="blurScheduleInput"
          />
          <span>分钟</span>
        </label>
        <p class="chef-settings-note"
          >以预约开始时间为准；{{ stoves.length ? '由主屏修改，各屏共享' : '由厨房负责人修改' }}</p
        >
        <button type="button" class="chef-header-button" @click="playNewDishSound">
          <i class="fas fa-volume-up" aria-hidden="true"></i>测试提示音
        </button>
        <p v-if="stoves.length" class="chef-settings-note"
          >同一厨房开启一个主屏即可自动分配菜品；超过 60 秒未收到更新，会提醒主屏已断开连接。</p
        >
      </div>
    </header>

    <main class="chef-main">
      <div v-if="soundError" class="chef-notice" role="status">
        <span>{{ soundError }}</span>
        <button type="button" class="chef-button chef-button-secondary" @click="playNewDishSound">
          启用提示音
        </button>
      </div>
      <div v-if="stoves.length && masterNotice" class="chef-notice" role="status">
        {{ masterNotice }}
      </div>
      <div v-if="selectionMessage || errorMessage" class="chef-notice" role="status">
        {{ errorMessage || selectionMessage }}
      </div>
      <div
        v-if="kitchenContextReady && !stoves.length && !isKitchenChef"
        class="chef-notice"
        role="status"
      >
        当前可查看整个厨房的制作任务，暂不能操作。
      </div>
      <div v-if="stoves.length && !selectedStove" class="chef-notice" role="status">
        暂未安排你的制作工位，当前无法查看个人制作任务。请联系负责人确认。
      </div>
      <div
        v-else-if="canControlShift && !stoves.length && !shiftEnabled"
        class="chef-notice"
        role="status"
      >
        当前未开班，可查看制作任务；点击“上班启用”后自动开始制作。
      </div>
      <div v-else-if="selectedStove && !selectedStoveEnabled" class="chef-notice" role="status">
        <span
          >当前已停止接单，点击“上班启用”后参与分配。
          <template v-if="visibleTasks.length"
            >还有 {{ visibleTasks.length }} 道菜等待转交。</template
          >
        </span>
        <button
          v-if="visibleTasks.length"
          type="button"
          class="chef-button chef-button-secondary"
          :disabled="loading || scheduleSaving || stoveBusy || busyTaskId !== null || masterBusy"
          @click="retryHandover"
        >
          {{ stoveBusy ? '转交中…' : '重试转交' }}
        </button>
      </div>
      <div v-if="stoves.length && !enabledStoves.length" class="chef-notice" role="status">
        当前暂无接单中的厨师，菜品将继续等待分配。
      </div>
      <div v-if="stoves.length && unassignedCount" class="chef-notice" role="status">
        <span
          >待分配 {{ unassignedCount }} 道菜。
          <template v-if="!isMaster">请保持自动分配主屏开启。</template>
          <template v-else-if="assignmentMessage">{{ assignmentMessage }}</template>
        </span>
      </div>
      <div v-if="assignmentMessage && !unassignedCount" class="chef-notice" role="status">{{
        assignmentMessage
      }}</div>

      <section class="chef-section" aria-labelledby="chef-cooking-title">
        <div class="chef-section-heading chef-cooking-heading">
          <h2 id="chef-cooking-title"
            ><i class="fas fa-utensils" aria-hidden="true"></i>正在制作
            <span class="chef-count chef-count-active">{{ cookingTasks.length }}</span></h2
          >
          <div
            class="chef-task-summary"
            :aria-label="stoves.length ? '当前厨师菜品任务汇总' : '当前厨房菜品任务汇总'"
          >
            <span
              >合计
              <strong>{{ cookingTasks.length + pendingTasks.length + pausedTasks.length }}</strong>
              道</span
            >
            <span class="chef-summary-cooking"
              >制作中 <strong>{{ cookingTasks.length }}</strong></span
            >
            <span
              >待制作 <strong>{{ pendingTasks.length }}</strong>
              <template v-if="!stoves.length">
                道 / <strong>{{ pendingPortions }}</strong
                >份
              </template></span
            >
            <span class="chef-summary-paused"
              >已暂停 <strong>{{ pausedTasks.length }}</strong></span
            >
          </div>
          <span class="chef-section-hint">完成后进入厨房出餐流程</span>
        </div>
        <div v-if="!cookingTasks.length" class="chef-empty">
          <i class="fas fa-utensils" aria-hidden="true"></i>当前没有正在制作的菜品
        </div>
        <div v-else class="chef-cooking-list">
          <article
            v-for="task in cookingTasks"
            :key="task.orderOutId"
            class="chef-cooking-card"
            :class="{ 'is-overdue': isOverdue(task) }"
          >
            <div class="chef-task-content">
              <button
                v-if="task.imageUrl"
                type="button"
                class="chef-food-image-button"
                :aria-label="`放大查看${task.shopFoodName || task.foodName}图片`"
                title="点击放大查看"
                @click="previewFoodImage(task)"
              >
                <img
                  :src="task.imageUrl"
                  :alt="task.shopFoodName || task.foodName"
                  class="chef-food-image"
                />
              </button>
              <div class="chef-task-info">
                <div class="chef-task-meta">
                  <strong class="chef-table">{{ task.tableName || '无桌号' }}</strong>
                  <strong class="chef-table chef-order-number"
                    >#{{ shortOrderNo(task.orderNo) }}</strong
                  >
                  <span>{{ formatClock(task.createTime) }}</span>
                  <span
                    :class="{ 'chef-dine-type-highlight': dineLabel(task.dineType) !== '堂食' }"
                    >{{ dineLabel(task.dineType) }}</span
                  >
                  <span v-if="appointmentLabel(task)" class="chef-appointment-time">{{
                    appointmentLabel(task)
                  }}</span>
                  <span class="chef-priority" :class="priorityClass(task)">{{
                    priorityLabel(task)
                  }}</span>
                </div>
                <div class="chef-food-heading">
                  <h3>{{ foodDisplayName(task) }}</h3>
                  <strong class="chef-quantity">× {{ task.quantity }}</strong>
                  <span v-if="task.foodCookDesc?.trim()" class="chef-food-cook">{{
                    task.foodCookDesc
                  }}</span>
                </div>
                <p v-if="taskOrderRemark(task)" class="chef-task-notes"
                  ><i class="fas fa-exclamation-circle" aria-hidden="true"></i>备注：{{
                    taskOrderRemark(task)
                  }}</p
                >
              </div>
            </div>
            <div v-if="isOverdue(task)" class="chef-task-timing is-overdue">
              <strong>{{ elapsed(task.startTime) }}</strong>
              <span>已制作 · 标准 {{ task.cookTime }} 分钟</span>
              <span class="chef-overdue-label">已超时</span>
            </div>
            <div class="chef-card-actions">
              <button
                v-if="selectedStove"
                type="button"
                class="chef-button chef-button-secondary"
                :disabled="taskActionsDisabled"
                @click="openTransfer(task)"
                >转交</button
              >
              <button
                type="button"
                class="chef-button chef-button-pause"
                :disabled="taskActionsDisabled"
                @click="changeTaskStatus(task, 9)"
                >暂停</button
              >
              <button
                type="button"
                class="chef-button chef-button-complete"
                :disabled="taskActionsDisabled"
                @click="changeTaskStatus(task, 10)"
                ><i class="fas fa-check" aria-hidden="true"></i>完成</button
              >
            </div>
          </article>
        </div>
      </section>

      <section class="chef-section" aria-labelledby="chef-pending-title">
        <div class="chef-section-heading">
          <h2 id="chef-pending-title"
            >接下来做 <span class="chef-count">{{ pendingTasks.length }}</span></h2
          >
          <span class="chef-section-hint">优先级优先 · 同级按等待时间</span>
        </div>
        <div v-if="!pendingTasks.length" class="chef-empty">暂无待制作菜品</div>
        <div v-else class="chef-queue">
          <div class="chef-queue-heading" aria-hidden="true">
            <span>顺序</span><span>订单 / 时间</span><span>桌号</span><span>菜品 / 规格 / 份数</span
            ><span>优先级 / 类型</span><span class="chef-align-right">操作</span>
          </div>
          <article
            v-for="(task, index) in pendingTasks"
            :key="task.orderOutId"
            class="chef-pending-row"
          >
            <strong class="chef-queue-rank" :class="{ 'is-next': index === 0 }">{{
              index + 1
            }}</strong>
            <div class="chef-queue-order"
              ><strong class="chef-order-number">#{{ shortOrderNo(task.orderNo) }}</strong
              ><span>{{ formatClock(task.createTime) }}</span></div
            >
            <strong class="chef-table">{{ task.tableName || '无桌号' }}</strong>
            <div class="chef-queue-food">
              <div class="chef-food-heading">
                <h3>{{ foodDisplayName(task) }}</h3
                ><strong class="chef-quantity">× {{ task.quantity }}</strong>
                <span v-if="task.foodCookDesc?.trim()" class="chef-food-cook">{{
                  task.foodCookDesc
                }}</span>
              </div>
              <p v-if="taskOrderRemark(task)" class="chef-task-notes"
                >备注：{{ taskOrderRemark(task) }}</p
              >
            </div>
            <div class="chef-queue-tags"
              ><span class="chef-priority" :class="priorityClass(task)">{{
                priorityLabel(task)
              }}</span
              ><span
                class="chef-dine-type"
                :class="{ 'chef-dine-type-highlight': dineLabel(task.dineType) !== '堂食' }"
                >{{ dineLabel(task.dineType) }}</span
              ><span v-if="appointmentLabel(task)" class="chef-appointment-time">{{
                appointmentLabel(task)
              }}</span></div
            >
            <div class="chef-pending-actions">
              <button
                v-if="selectedStove"
                type="button"
                class="chef-button chef-button-secondary"
                :disabled="taskActionsDisabled"
                @click="openTransfer(task)"
                >转交</button
              >
              <button
                type="button"
                class="chef-button chef-button-start"
                :disabled="taskActionsDisabled"
                @click="changeTaskStatus(task, 5)"
                >开始</button
              >
            </div>
          </article>
        </div>
      </section>

      <section v-if="pausedTasks.length" class="chef-section" aria-labelledby="chef-paused-title">
        <div class="chef-section-heading"
          ><h2 id="chef-paused-title"
            >已暂停 <span class="chef-count chef-count-paused">{{ pausedTasks.length }}</span></h2
          ></div
        >
        <div class="chef-paused-list">
          <article v-for="task in pausedTasks" :key="task.orderOutId" class="chef-paused-row">
            <div class="chef-task-info">
              <div class="chef-task-meta"
                ><strong class="chef-table">{{ task.tableName || '无桌号' }}</strong
                ><strong class="chef-order-number">#{{ shortOrderNo(task.orderNo) }}</strong
                ><span
                  :class="{ 'chef-dine-type-highlight': dineLabel(task.dineType) !== '堂食' }"
                  >{{ dineLabel(task.dineType) }}</span
                ><span v-if="appointmentLabel(task)" class="chef-appointment-time">{{
                  appointmentLabel(task)
                }}</span></div
              >
              <div class="chef-food-heading"
                ><h3>{{ foodDisplayName(task) }}</h3
                ><strong class="chef-quantity">× {{ task.quantity }}</strong>
                <span v-if="task.foodCookDesc?.trim()" class="chef-food-cook">{{
                  task.foodCookDesc
                }}</span></div
              >
              <p v-if="taskOrderRemark(task)" class="chef-task-notes"
                >备注：{{ taskOrderRemark(task) }}</p
              >
            </div>
            <div class="chef-pause-reason"
              ><strong>{{ task.operateStatus === 1 ? '服务员暂停' : '厨师暂停' }}</strong
              ><span>{{ task.operateStatus === 1 ? '等待服务员恢复' : '可继续制作' }}</span></div
            >
            <div class="chef-paused-actions">
              <button
                v-if="selectedStove"
                type="button"
                class="chef-button chef-button-secondary"
                :disabled="taskActionsDisabled"
                @click="openTransfer(task)"
                >转交</button
              >
              <button
                v-if="task.operateStatus !== 1 && task.kitchenStatus === 9"
                type="button"
                class="chef-button chef-button-start"
                :disabled="taskActionsDisabled"
                @click="changeTaskStatus(task, 5)"
                >继续</button
              >
            </div>
          </article>
        </div>
      </section>
      <section
        v-if="scheduledTasks.length"
        class="chef-section"
        aria-labelledby="chef-scheduled-title"
      >
        <div class="chef-section-heading">
          <h2 id="chef-scheduled-title">
            <button
              type="button"
              class="chef-scheduled-toggle"
              :aria-expanded="scheduledExpanded"
              aria-controls="chef-scheduled-queue"
              @click="scheduledExpanded = !scheduledExpanded"
            >
              <i
                :class="scheduledExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"
                aria-hidden="true"
              ></i>
              预约待制作 <span class="chef-count">{{ scheduledTasks.length }}</span>
              <span class="chef-section-hint">{{ scheduledExpanded ? '收起' : '展开' }}</span>
            </button>
          </h2>
          <span class="chef-section-hint"
            >厨房公共预约 · 提前{{ scheduleSettings.advanceMinutes }}分钟进入待制作</span
          >
        </div>
        <div v-show="scheduledExpanded" id="chef-scheduled-queue" class="chef-queue">
          <div class="chef-queue-heading" aria-hidden="true">
            <span>顺序</span><span>订单 / 时间</span><span>桌号</span><span>菜品 / 规格 / 份数</span
            ><span>优先级 / 类型</span><span class="chef-align-right">操作</span>
          </div>
          <article
            v-for="(task, index) in scheduledTasks"
            :key="task.orderOutId"
            class="chef-pending-row"
          >
            <strong class="chef-queue-rank">{{ index + 1 }}</strong>
            <div class="chef-queue-order" :title="task.orderNo"
              ><strong class="chef-order-number">#{{ shortOrderNo(task.orderNo) }}</strong
              ><span>{{ formatClock(task.createTime) }}</span></div
            >
            <strong class="chef-table">{{ task.tableName || '无桌号' }}</strong>
            <div class="chef-queue-food">
              <div class="chef-food-heading">
                <h3>{{ foodDisplayName(task) }}</h3
                ><strong class="chef-quantity">× {{ task.quantity }}</strong>
                <span v-if="task.foodCookDesc?.trim()" class="chef-food-cook">{{
                  task.foodCookDesc
                }}</span>
              </div>
              <p class="chef-task-notes">
                预约：{{ task.selfPickDate?.slice(0, 10) }} {{ task.selfPickTime }}
              </p>
              <p class="chef-task-notes">
                进入待制作：{{ scheduleDateTime(kitchenReleaseTime(task, scheduleSettings)) }} ·
                {{ scheduleCountdown(task) }}
              </p>
              <p v-if="task.stoveId" class="chef-task-notes">负责厨师：{{ task.stoveName }}</p>
              <p v-if="taskOrderRemark(task)" class="chef-task-notes"
                >备注：{{ taskOrderRemark(task) }}</p
              >
            </div>
            <div class="chef-queue-tags"
              ><span class="chef-priority" :class="priorityClass(task)">{{
                priorityLabel(task)
              }}</span
              ><span
                class="chef-dine-type"
                :class="{ 'chef-dine-type-highlight': dineLabel(task.dineType) !== '堂食' }"
                >{{ dineLabel(task.dineType) }}</span
              ></div
            >
            <div class="chef-pending-actions">
              <button
                type="button"
                class="chef-button chef-button-start"
                :disabled="
                  taskActionsDisabled ||
                  Boolean(task.stoveId && String(task.stoveId) !== selectedStoveId)
                "
                @click="claimScheduledTask(task)"
                >{{
                  busyTaskId === task.orderOutId
                    ? '处理中…'
                    : task.stoveId || !stoves.length
                    ? '开始制作'
                    : '认领并开始'
                }}</button
              >
            </div>
          </article>
        </div>
      </section>
    </main>
    <van-dialog
      class="chef-transfer-modal"
      width="var(--chef-transfer-width)"
      :show="transferTask !== null"
      title="转交菜品"
      :show-confirm-button="false"
      :close-on-click-overlay="false"
    >
      <div class="chef-transfer-dialog">
        <div v-if="transferTask" class="chef-transfer-food">
          <span class="chef-transfer-table">{{ transferTask.tableName || '无桌号' }}</span>
          <div class="chef-transfer-food-heading">
            <strong>{{ foodDisplayName(transferTask) }}</strong>
            <strong class="chef-transfer-quantity">× {{ transferTask.quantity }}</strong>
          </div>
          <span v-if="transferTask.foodCookDesc?.trim()" class="chef-transfer-cook">{{
            transferTask.foodCookDesc
          }}</span>
        </div>
        <p class="chef-transfer-note">
          {{
            transferTask?.operateStatus === 1
              ? '转交后仍保持服务员暂停。'
              : transferTask?.kitchenStatus === 5
              ? '将停止当前制作，由接收厨师重新开始。'
              : '转交后由接收厨师开始制作。'
          }}
        </p>
        <label v-if="transferTargets.length">
          <span>接收厨师</span>
          <select v-model="transferStoveId" :disabled="transferSubmitting">
            <option value="" disabled>请选择厨师</option>
            <option
              v-for="stove in transferTargets"
              :key="stove.stoveId"
              :value="String(stove.stoveId)"
            >
              {{ stove.stoveName }}（{{ stoveTaskCount(stove.stoveId) }} 道菜）
            </option>
          </select>
        </label>
        <p v-else class="chef-transfer-note">暂无其他接单中的厨师可接收菜品，请稍后再试。</p>
        <div class="chef-transfer-actions">
          <van-button :disabled="transferSubmitting" @click="closeTransfer">取消</van-button>
          <van-button
            type="primary"
            :loading="transferSubmitting"
            :disabled="!transferStoveId"
            @click="confirmTransfer"
            >确认转交</van-button
          >
        </div>
      </div>
    </van-dialog>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { showConfirmDialog, showImagePreview, showToast } from 'vant';
  import { resolveAccessibleShopIds } from '@/constants/auth';
  import type {
    FoodOutDetailsListModel,
    KitchenStoveModel,
    KitchenScheduleSettingsModel,
  } from '@/model/kitchen';
  import { useRestaurantKitchen } from '@/composables/useRestaurantKitchen';
  import { useKitchenMaster } from '@/composables/useKitchenMaster';
  import {
    fetchConfiguredStoves,
    fetchKitchenChefName,
    fetchKitchenQueue,
    changeKitchenStatus,
    changeStoveStatus,
  } from '@/service/kitchen';
  import {
    defaultKitchenScheduleSettings,
    isValidKitchenAdvanceMinutes,
    isKitchenTaskScheduled,
    kitchenAppointmentTime,
    kitchenReleaseTime,
  } from '@/service/kitchenSchedule';
  import {
    fetchKitchenScheduleSettings,
    saveKitchenScheduleSettings,
  } from '@/service/kitchenMaster';
  import { releaseStoveTasks } from '@/service/kitchenDispatch';
  import { assignKitchenTasksAutomatically } from '@/service/kitchenAutoAssignment';
  import { selectKitchenStartBatch, startKitchenTasksTogether } from '@/service/kitchenTaskBatch';
  import { distributeStove } from '@/service/order';
  import { useAuthStore } from '@/store/auth';

  const authStore = useAuthStore();
  const chefName = computed(
    () => authStore.user?.displayName || authStore.user?.username || '厨师',
  );
  const { shops, currentShop, kitchens, currentKitchen, selectionMessage, loadShops } =
    useRestaurantKitchen('chef_screen_kitchen', 'session');
  const stoves = ref<KitchenStoveModel[]>([]);
  const accessibleShops = computed(() => {
    const shopIds = resolveAccessibleShopIds(authStore.user?.roles || []);
    return shopIds
      ? shops.value.filter((shop) => shopIds.includes(Number(shop.shopId)))
      : shops.value;
  });
  const foodDisplayName = (task: FoodOutDetailsListModel): string => {
    const name = task.shopFoodName || task.foodName;
    const spec = task.foodSpecName?.trim();
    return spec && spec !== '标准' ? `${name}（${spec}）` : name;
  };
  const tasks = ref<FoodOutDetailsListModel[]>([]);
  const scheduleSettings = ref<KitchenScheduleSettingsModel>(defaultKitchenScheduleSettings());
  const scheduleSaving = ref(false);
  const scheduleMinutesInput = ref('30');
  const scheduleInputFocused = ref(false);
  const scheduleInputDirty = ref(false);
  const scheduledExpanded = ref(false);
  const scheduleReady = ref(false);
  // 使用每次成功刷新时的时间推进队列，倒计时仍按秒更新。
  const scheduleNow = ref(Date.now());
  const isScheduled = (task: FoodOutDetailsListModel): boolean =>
    isKitchenTaskScheduled(task, scheduleSettings.value, scheduleNow.value);
  const scheduledTasks = computed(() =>
    tasks.value
      .filter(isScheduled)
      .sort(
        (a, b) =>
          (kitchenAppointmentTime(a) || 0) - (kitchenAppointmentTime(b) || 0) ||
          a.orderOutId - b.orderOutId,
      ),
  );
  const canEditSchedule = computed(
    () =>
      kitchenContextReady.value &&
      scheduleReady.value &&
      (stoves.value.length ? isMaster.value : isKitchenChef.value),
  );
  const scheduleDateTime = (timestamp: number | null): string =>
    timestamp === null
      ? '--'
      : new Intl.DateTimeFormat('zh-CN', {
          timeZone: 'Asia/Shanghai',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(timestamp);
  const scheduleCountdown = (task: FoodOutDetailsListModel): string => {
    const minutes = Math.max(
      0,
      Math.ceil(((kitchenReleaseTime(task, scheduleSettings.value) || 0) - now.value) / 60000),
    );
    return minutes
      ? `${Math.floor(minutes / 60)}小时${minutes % 60}分钟后进入待制作`
      : '即将进入待制作';
  };
  const selectedStoveId = ref('');
  const kitchenChefName = ref('');
  const kitchenContextReady = ref(false);
  // 无灶台时由当前页面控制开班；仅刷新恢复，新打开或切换厨房仍需主动开班。
  const kitchenShiftEnabled = ref(false);
  let restoreShiftPending = performance
    .getEntriesByType('navigation')
    .some((entry) => 'type' in entry && entry.type === 'reload');
  const now = ref(Date.now());
  const {
    isMaster,
    masterSelected,
    masterBusy,
    masterNotice,
    claimMaster,
    stopMaster,
    syncMaster,
    pauseMaster,
  } = useKitchenMaster(
    currentShop,
    currentKitchen,
    now,
    () => ({ account: authStore.user?.username || '', accountName: chefName.value }),
    (hasOtherMasterOnDevice) => {
      // 同设备另一页面接管时，保留新主屏的恢复设置。
      if (!hasOtherMasterOnDevice) writeStored('master', 'false');
      showToast('当前页面已停止自动分配');
    },
  );
  const showSettings = ref(false);
  const loading = ref(false);
  const refreshing = ref(false);
  const manualRefreshing = ref(false);
  const errorMessage = ref('');
  const assignmentMessage = ref('');
  const busyTaskId = ref<number | null>(null);
  const stoveBusy = ref(false);
  const transferTask = ref<FoodOutDetailsListModel | null>(null);
  const transferStoveId = ref('');
  const transferSubmitting = ref(false);
  let restoreMasterPending = false;
  let refreshTimer: ReturnType<typeof setInterval> | undefined;
  let clockTimer: ReturnType<typeof setInterval> | undefined;
  let requestVersion = 0;
  let newDishAudio: HTMLAudioElement | null = null;
  let priorityAudio: HTMLAudioElement | null = null;
  const soundError = ref('');
  let soundScope = '';
  let lastVisibleTaskPriorities: Map<number, number> | null = null;

  const playKitchenSound = async (audio: HTMLAudioElement | null): Promise<void> => {
    try {
      if (!audio) throw new Error('提示音尚未加载');
      audio.currentTime = 0;
      await audio.play();
      soundError.value = '';
    } catch (error) {
      console.warn('播放厨房提示音失败', error);
      soundError.value =
        error instanceof Error && error.name === 'NotAllowedError'
          ? '浏览器暂未允许播放提示音，请点击“启用提示音”。'
          : '提示音暂时无法播放，请点击“启用提示音”重试；仍无声音时请刷新页面。';
    }
  };
  const playNewDishSound = (): Promise<void> => playKitchenSound(newDishAudio);

  const selectedKitchenName = computed(
    () => kitchens.value.find((item) => item.id === currentKitchen.value)?.name || '',
  );
  const selectedStoveName = computed(
    () =>
      stoves.value.find((item) => String(item.stoveId) === selectedStoveId.value)?.stoveName || '',
  );
  const chefLoginNames = computed(() =>
    [authStore.user?.displayName, authStore.user?.username]
      .filter((name): name is string => Boolean(name?.trim()))
      .map((name) => name.trim()),
  );
  const matchesKitchenChef = (configuredNames: string): boolean =>
    configuredNames
      .split(',')
      .map((name) => name.trim())
      .some((name) => Boolean(name) && chefLoginNames.value.includes(name));
  const ownStoves = computed(() =>
    stoves.value.filter((stove) => chefLoginNames.value.includes(stove.stoveName.trim())),
  );
  const isKitchenChef = computed(() => matchesKitchenChef(kitchenChefName.value));
  const selectedStove = computed(() =>
    ownStoves.value.find((stove) => String(stove.stoveId) === selectedStoveId.value),
  );
  const selectedStoveEnabled = computed(() => selectedStove.value?.stoveStatus === 0);
  const canControlShift = computed(
    () =>
      kitchenContextReady.value &&
      (stoves.value.length ? Boolean(selectedStove.value) : isKitchenChef.value),
  );
  const shiftEnabled = computed(() =>
    stoves.value.length ? selectedStoveEnabled.value : kitchenShiftEnabled.value,
  );
  const canOperateCurrentKitchen = computed(() => canControlShift.value && shiftEnabled.value);
  const enabledStoves = computed(() => stoves.value.filter((stove) => stove.stoveStatus === 0));
  const transferTargets = computed(() =>
    enabledStoves.value.filter((stove) => String(stove.stoveId) !== selectedStoveId.value),
  );
  const stoveTaskCount = (stoveId: number): number =>
    tasks.value.filter((task) => Number(task.stoveId) === stoveId && !isScheduled(task)).length;
  const taskActionsDisabled = computed(
    () =>
      loading.value ||
      stoveBusy.value ||
      Boolean(errorMessage.value) ||
      scheduleSaving.value ||
      !scheduleReady.value ||
      busyTaskId.value !== null ||
      !canOperateCurrentKitchen.value,
  );
  const visibleTasks = computed(() =>
    tasks.value.filter(
      (task) =>
        !isScheduled(task) &&
        (!stoves.value.length || String(task.stoveId) === selectedStoveId.value),
    ),
  );
  const cookingTasks = computed(() =>
    visibleTasks.value.filter((task) => task.kitchenStatus === 5 && task.operateStatus !== 1),
  );
  const pendingTasks = computed(() =>
    visibleTasks.value
      .filter((task) => task.kitchenStatus === 0 && task.operateStatus !== 1)
      .sort((a, b) => {
        const priorityDifference = effectivePriority(b) - effectivePriority(a);
        if (priorityDifference) return priorityDifference;
        const timeDifference = timeValue(a.createTime) - timeValue(b.createTime);
        return timeDifference || a.orderOutId - b.orderOutId;
      }),
  );
  const pausedTasks = computed(() =>
    visibleTasks.value.filter((task) => task.operateStatus === 1 || task.kitchenStatus === 9),
  );
  const pendingPortions = computed(() =>
    pendingTasks.value.reduce((sum, task) => sum + (task.quantity || 0), 0),
  );
  const unassignedCount = computed(
    () => tasks.value.filter((task) => !task.stoveId && !isScheduled(task)).length,
  );
  const nextAutoStartTask = computed(() =>
    cookingTasks.value.length
      ? cookingTasks.value.find(
          (task) =>
            selectKitchenStartBatch(task, visibleTasks.value, scheduleSettings.value).length > 1,
        )
      : pendingTasks.value[0],
  );
  const canAutoStart = computed(
    () =>
      Boolean(currentShop.value && currentKitchen.value && currentKitchen.value !== 'all') &&
      accessibleShops.value.some((shop) => String(shop.shopId) === currentShop.value) &&
      !taskActionsDisabled.value &&
      !refreshing.value &&
      Boolean(nextAutoStartTask.value),
  );

  const timeValue = (value?: string): number => {
    if (!value) return 0;
    const parsed = new Date(value.replace(' ', 'T')).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const formatClock = (value?: string): string => {
    const parsed = timeValue(value);
    return parsed
      ? new Date(parsed).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      : '--:--';
  };
  const elapsed = (value?: string): string => {
    if (!timeValue(value)) return '00:00';
    const seconds = Math.max(0, Math.floor((now.value - timeValue(value)) / 1000));
    const minutes = Math.floor(seconds / 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  };
  const isOverdue = (task: FoodOutDetailsListModel): boolean =>
    Boolean(
      task.cookTime &&
        timeValue(task.startTime) &&
        now.value - timeValue(task.startTime) > task.cookTime * 60000,
    );
  const shortOrderNo = (value: string): string => value?.slice(-4) || '----';
  const dineLabel = (value?: number): string => {
    if (value === 3) return '外卖';
    if (value === 4) return '打包';
    if (value === 2) return '自提';
    if (value === 5) return '预定';
    return '堂食';
  };
  const appointmentLabel = (task: FoodOutDetailsListModel): string => {
    const day = task.selfPickDate?.trim().match(/^\d{4}-\d{2}-(\d{2})(?:$|[T\s])/)?.[1];
    const date = day ? `${Number(day)}日` : '';
    const time = task.selfPickTime?.trim().replace(/\s*-\s*/g, '–') || '';
    return [date, time].filter(Boolean).join(' ');
  };
  const taskOrderRemark = (task: FoodOutDetailsListModel): string =>
    task.orderRemark?.trim() || task.description?.trim() || '';
  const previewFoodImage = (task: FoodOutDetailsListModel): void => {
    if (!task.imageUrl) return;
    showImagePreview({
      images: [task.imageUrl],
      closeable: true,
      showIndex: false,
      closeOnClickImage: false,
    });
  };
  const effectivePriority = (task: FoodOutDetailsListModel): number =>
    task.operateStatus === 10 ? Math.max(task.priority || 1, 999) : task.priority || 1;
  const priorityLabel = (task: FoodOutDetailsListModel): string => {
    if (effectivePriority(task) >= 999) return '加急';
    if (effectivePriority(task) >= 666) return '叫菜';
    return '普通';
  };
  const priorityClass = (task: FoodOutDetailsListModel): string => {
    if (effectivePriority(task) >= 999) return 'chef-priority-urgent';
    if (effectivePriority(task) >= 666) return 'chef-priority-called';
    return 'bg-slate-100 text-slate-600';
  };

  const storageKey = (kind: string): string =>
    `chef_screen_${kind}_${currentShop.value}_${currentKitchen.value}`;
  const readStored = (kind: string): string => {
    try {
      return localStorage.getItem(storageKey(kind)) || '';
    } catch (error) {
      console.warn('读取厨师大屏设置失败', error);
      return '';
    }
  };
  const writeStored = (kind: string, value: string): void => {
    try {
      localStorage.setItem(storageKey(kind), value);
    } catch (error) {
      console.warn('保存厨师大屏设置失败', error);
    }
  };

  const toggleMaster = async (event: Event): Promise<void> => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (!input) return;
    if (input.checked) {
      const claimed = await claimMaster();
      if (claimed) {
        writeStored('master', 'true');
        void refreshData();
      }
    } else {
      writeStored('master', 'false');
      await stopMaster();
    }
    input.checked = masterSelected.value;
  };
  const takeoverMaster = async (): Promise<void> => {
    const shop = currentShop.value;
    const kitchen = currentKitchen.value;
    const statusMessage = masterNotice.value || '暂时无法确认当前主屏是否正在运行';
    const confirmed = await showConfirmDialog({
      title: '强制接管主屏',
      message: `${statusMessage}\n\n接管后，将由当前页面负责自动分配，原主屏会停止分配。确认接管？`,
    }).then(
      () => true,
      () => false,
    );
    if (!confirmed || shop !== currentShop.value || kitchen !== currentKitchen.value) return;
    const claimed = await claimMaster(true);
    if (claimed) {
      writeStored('master', 'true');
      showToast('已接管自动分配主屏');
      void refreshData();
    }
  };

  const loadQueue = async (kitchenId: number): Promise<FoodOutDetailsListModel[]> => {
    const response = await fetchKitchenQueue({
      pageNum: 1,
      pageSize: 999,
      data: { kitchenId },
    });
    if (response.code !== 200) throw new Error(response.msg || '加载任务失败');
    return (response.result?.list || [])
      .flatMap((order) =>
        (order.detailsList || []).map((task) => ({
          ...task,
          dineType: order.dineType ?? task.dineType,
          selfPickDate: order.selfPickDate || task.selfPickDate,
          selfPickTime: order.selfPickTime || task.selfPickTime,
          orderRemark: order.description?.trim() || task.description?.trim() || '',
        })),
      )
      .filter(
        (task) =>
          Number(task.shopId) === Number(currentShop.value) &&
          Number(task.kitchenId) === kitchenId &&
          ![8, 9, 999].includes(task.status) &&
          task.kitchenStatus !== 10,
      );
  };

  const refreshData = async (showLoading = false): Promise<void> => {
    if (!currentShop.value || !currentKitchen.value || currentKitchen.value === 'all') return;
    if (!accessibleShops.value.some((shop) => String(shop.shopId) === currentShop.value)) return;
    const version = ++requestVersion;
    const kitchenId = Number(currentKitchen.value);
    refreshing.value = true;
    loading.value = showLoading;
    try {
      const [stoveResponse, queue, settings] = await Promise.all([
        fetchConfiguredStoves(kitchenId),
        loadQueue(kitchenId),
        fetchKitchenScheduleSettings(kitchenId, Number(currentShop.value)),
      ]);
      if (version !== requestVersion) return;
      if (stoveResponse.code !== 200)
        throw new Error(stoveResponse.msg || '暂时无法读取厨师接单情况，请刷新重试');
      const nextStoves = (stoveResponse.result?.list || [])
        .filter((stove) => Number(stove.kitchenId) === kitchenId)
        .sort((a, b) => a.stoveId - b.stoveId);
      const nextChefName = nextStoves.length
        ? ''
        : await fetchKitchenChefName(Number(currentShop.value), kitchenId);
      if (version !== requestVersion) return;
      if (JSON.stringify(stoves.value) !== JSON.stringify(nextStoves)) stoves.value = nextStoves;
      kitchenChefName.value = nextChefName;
      kitchenContextReady.value = true;
      if (stoves.value.length) {
        if (!ownStoves.value.some((stove) => String(stove.stoveId) === selectedStoveId.value)) {
          const stored = readStored('stove');
          selectedStoveId.value = ownStoves.value.some((stove) => String(stove.stoveId) === stored)
            ? stored
            : ownStoves.value.length
            ? String(ownStoves.value[0].stoveId)
            : '';
        }
      } else {
        selectedStoveId.value = '';
      }
      if (stoves.value.length) {
        if (restoreMasterPending) {
          restoreMasterPending = false;
          // 自动恢复申请失败不能覆盖同设备其它页面的主屏设置。
          if (await claimMaster()) writeStored('master', 'true');
        } else {
          await syncMaster();
        }
      }
      if (version !== requestVersion) return;
      let nextQueue = queue;
      let nextAssignmentMessage = '';
      if (isMaster.value && stoves.value.length && queue.some((task) => !task.stoveId)) {
        const result = await assignKitchenTasksAutomatically({
          kitchenId,
          scheduleSettings: settings,
          tasks: queue,
          stoves: stoves.value,
          readQueue: () => loadQueue(kitchenId),
          shouldContinue: () => version === requestVersion && isMaster.value,
          verifyMaster: syncMaster,
        });
        if (version !== requestVersion) return;
        nextAssignmentMessage = result.message;
        if (result.assigned) nextQueue = await loadQueue(kitchenId);
      }
      if (version !== requestVersion) return;
      // 预约配置、时间和队列一起提交，避免刷新中途用新规则展示旧队列。
      if (scheduleSettings.value.advanceMinutes !== settings.advanceMinutes)
        scheduleSettings.value = settings;
      scheduleNow.value = Date.now();
      scheduleReady.value = true;
      // 保留旧队列直到本次读取和分配结束，未变化的数据不触发整组更新。
      if (JSON.stringify(tasks.value) !== JSON.stringify(nextQueue)) tasks.value = nextQueue;
      assignmentMessage.value = nextAssignmentMessage;
      errorMessage.value = '';
    } catch (error) {
      if (version !== requestVersion) return;
      pauseMaster();
      scheduleReady.value = false;
      console.error('加载厨师任务失败', error);
      errorMessage.value = error instanceof Error ? error.message : '加载厨师任务失败';
    } finally {
      if (version === requestVersion) {
        loading.value = false;
        refreshing.value = false;
      }
    }
  };

  const refreshManually = async (): Promise<void> => {
    if (manualRefreshing.value || loading.value || stoveBusy.value || busyTaskId.value !== null)
      return;
    manualRefreshing.value = true;
    try {
      await refreshData();
    } finally {
      manualRefreshing.value = false;
    }
  };

  const invalidateRefresh = (): void => {
    requestVersion++;
    refreshing.value = false;
  };

  const releaseCurrentStoveTasks = async (stoveId: number): Promise<void> => {
    const kitchenId = Number(currentKitchen.value);
    const queue = await loadQueue(kitchenId);
    const result = await releaseStoveTasks(
      queue.filter((task) => Number(task.stoveId) === stoveId),
    );
    if (result.failedCount)
      throw new Error(`${result.failedCount} 道菜未能转交，请点击“重试转交”。`);
    const latestQueue = await loadQueue(kitchenId);
    const remaining = latestQueue.filter((task) => Number(task.stoveId) === stoveId).length;
    if (remaining) throw new Error(`还有 ${remaining} 道菜仍未转交，请点击“重试转交”。`);
  };

  const shiftStorageKey = (): string =>
    `chef_screen_shift_${JSON.stringify([
      authStore.user?.username || '',
      currentShop.value,
      currentKitchen.value,
    ])}`;
  const saveKitchenShift = (): void => {
    try {
      sessionStorage.setItem(shiftStorageKey(), String(kitchenShiftEnabled.value));
    } catch (error) {
      console.warn('保存厨师大屏开班状态失败', error);
    }
  };
  const restoreKitchenShift = (): void => {
    if (!currentShop.value || !currentKitchen.value || currentKitchen.value === 'all') return;
    if (restoreShiftPending) {
      restoreShiftPending = false;
      try {
        kitchenShiftEnabled.value = sessionStorage.getItem(shiftStorageKey()) === 'true';
      } catch (error) {
        console.warn('恢复厨师大屏开班状态失败', error);
      }
    }
    saveKitchenShift();
  };

  const toggleShiftStatus = async (): Promise<void> => {
    if (!canControlShift.value || loading.value || stoveBusy.value || busyTaskId.value !== null)
      return;
    if (!stoves.value.length) {
      invalidateRefresh();
      kitchenShiftEnabled.value = !kitchenShiftEnabled.value;
      saveKitchenShift();
      showToast(kitchenShiftEnabled.value ? '已开始接单' : '已停止接单，不再自动开始制作');
      return;
    }
    const stove = selectedStove.value;
    if (!stove || loading.value || stoveBusy.value || busyTaskId.value !== null) return;
    invalidateRefresh();
    const stoveStatus = stove.stoveStatus === 0 ? 1 : 0;
    stoveBusy.value = true;
    let failureMessage = '';
    try {
      if (stoveStatus === 1) {
        const queue = await loadQueue(Number(currentKitchen.value));
        const cookingCount = queue.filter(
          (task) => Number(task.stoveId) === stove.stoveId && task.kitchenStatus === 5,
        ).length;
        if (cookingCount) {
          const confirmed = await showConfirmDialog({
            title: '确认下班停止',
            message: `当前有 ${cookingCount} 道菜正在制作。停止接单后，未完成的菜品将等待其他厨师接手，并重新开始制作。确认停止接单？`,
            confirmButtonText: '确认停止',
            cancelButtonText: '继续制作',
          }).then(
            () => true,
            () => false,
          );
          if (!confirmed) return;
        }
      }
      const response = await changeStoveStatus({ stoveId: stove.stoveId, stoveStatus });
      if (response.code !== 200)
        throw new Error(response.msg || '未能切换接单状态，请刷新查看后重试');
      if (stoveStatus === 1) await releaseCurrentStoveTasks(stove.stoveId);
      showToast(stoveStatus === 0 ? '已开始接单' : '已停止接单，未完成菜品将等待其他厨师接手');
    } catch (error) {
      console.error('更新厨师启停状态失败', error);
      failureMessage = error instanceof Error ? error.message : '操作失败，请重试';
      showToast(failureMessage);
    } finally {
      await refreshData();
      if (failureMessage) errorMessage.value = failureMessage;
      stoveBusy.value = false;
    }
  };

  const retryHandover = async (): Promise<void> => {
    const stove = selectedStove.value;
    if (
      !stove ||
      selectedStoveEnabled.value ||
      loading.value ||
      stoveBusy.value ||
      busyTaskId.value !== null
    )
      return;
    invalidateRefresh();
    stoveBusy.value = true;
    let failureMessage = '';
    try {
      const response = await fetchConfiguredStoves(Number(currentKitchen.value));
      if (response.code !== 200)
        throw new Error(response.msg || '暂时无法确认厨师是否接单，请刷新重试');
      if (response.result.list.find((item) => item.stoveId === stove.stoveId)?.stoveStatus !== 1)
        throw new Error('厨师的接单情况已变化，请刷新查看');
      await releaseCurrentStoveTasks(stove.stoveId);
      showToast('未完成菜品将等待其他厨师接手');
    } catch (error) {
      console.error('退回厨师菜品失败', error);
      failureMessage = error instanceof Error ? error.message : '退回失败，请重试';
      showToast(failureMessage);
    } finally {
      await refreshData();
      if (failureMessage) errorMessage.value = failureMessage;
      stoveBusy.value = false;
    }
  };

  const editScheduleMinutes = (event: Event): void => {
    if (!(event.target instanceof HTMLInputElement)) return;
    scheduleMinutesInput.value = event.target.value;
    scheduleInputDirty.value =
      scheduleMinutesInput.value !== String(scheduleSettings.value.advanceMinutes);
  };

  const blurScheduleInput = (event: KeyboardEvent): void => {
    if (event.target instanceof HTMLInputElement) event.target.blur();
  };

  const finishScheduleInput = (): void => {
    scheduleInputFocused.value = false;
    void updateSchedule();
  };

  const updateSchedule = async (): Promise<void> => {
    const advanceMinutes =
      scheduleMinutesInput.value.trim() === '' ? NaN : Number(scheduleMinutesInput.value);
    if (!isValidKitchenAdvanceMinutes(advanceMinutes)) {
      showToast('请输入非负整数分钟数');
      return;
    }
    if (advanceMinutes === scheduleSettings.value.advanceMinutes) {
      scheduleInputDirty.value = false;
      return;
    }
    if (
      !canEditSchedule.value ||
      scheduleSaving.value ||
      stoveBusy.value ||
      busyTaskId.value !== null
    )
      return;
    invalidateRefresh();
    const version = requestVersion;
    const kitchenId = Number(currentKitchen.value);
    const shopId = Number(currentShop.value);
    scheduleSaving.value = true;
    try {
      const saved = await saveKitchenScheduleSettings(
        kitchenId,
        shopId,
        { advanceMinutes },
        async () => {
          const response = await fetchConfiguredStoves(kitchenId);
          if (response.code !== 200) throw new Error(response.msg || '读取厨房工位失败');
          if (version !== requestVersion) return false;
          if (response.result.list.some((stove) => Number(stove.kitchenId) === kitchenId))
            return syncMaster();
          const name = await fetchKitchenChefName(shopId, kitchenId);
          return version === requestVersion && matchesKitchenChef(name);
        },
      );
      if (version !== requestVersion) return;
      scheduleSettings.value = saved;
      scheduleMinutesInput.value = String(saved.advanceMinutes);
      scheduleInputDirty.value = false;
      await refreshData();
      if (errorMessage.value) throw new Error('设置已提交，请刷新核对结果');
      showToast(`预约菜品提前${saved.advanceMinutes}分钟进入待制作`);
    } catch (error) {
      console.error('保存预约制作设置失败', error);
      if (Number(currentKitchen.value) === kitchenId && Number(currentShop.value) === shopId) {
        errorMessage.value = error instanceof Error ? error.message : '保存预约制作设置失败';
        showToast(errorMessage.value);
      }
    } finally {
      scheduleSaving.value = false;
    }
  };

  const claimScheduledTask = async (task: FoodOutDetailsListModel): Promise<void> => {
    if (taskActionsDisabled.value) return;
    // 与普通待制作操作一致，点击时使后台刷新失效，再读取最新状态。
    invalidateRefresh();
    const version = requestVersion;
    const kitchenId = Number(currentKitchen.value);
    const shopId = Number(currentShop.value);
    const stoveId = selectedStoveId.value;
    const contextValid = (): boolean =>
      version === requestVersion && stoveId === selectedStoveId.value;
    busyTaskId.value = task.orderOutId;
    let submitted = false;
    let failureMessage = '';
    try {
      const [response, queue] = await Promise.all([
        fetchConfiguredStoves(kitchenId),
        loadQueue(kitchenId),
      ]);
      if (!contextValid()) return;
      if (response.code !== 200) throw new Error(response.msg || '读取厨师接单状态失败');
      const configured = response.result.list.filter(
        (stove) => Number(stove.kitchenId) === kitchenId,
      );
      const latest = queue.find((item) => item.orderOutId === task.orderOutId);
      if (
        !latest ||
        latest.kitchenStatus !== 0 ||
        latest.operateStatus === 1 ||
        (latest.stoveId && String(latest.stoveId) !== stoveId)
      )
        throw new Error('菜品已被认领或制作状态已变化，请刷新查看');
      if (configured.length) {
        const target = configured.find(
          (stove) =>
            String(stove.stoveId) === stoveId &&
            chefLoginNames.value.includes(stove.stoveName.trim()) &&
            stove.stoveStatus === 0,
        );
        if (!target) throw new Error('当前未接单，请上班启用后重试');
        if (!latest.stoveId) {
          submitted = true;
          const assigned = await distributeStove({
            orderOutId: task.orderOutId,
            stoveId: target.stoveId,
          });
          if (assigned.code !== 200) throw new Error(assigned.msg || '认领失败');
          if (!contextValid()) return;
          const reset = await changeKitchenStatus({
            orderOutId: task.orderOutId,
            kitchenStatus: 0,
          });
          if (reset.code !== 200) throw new Error(reset.msg || '已认领，暂未确认待制作状态');
        }
      } else {
        const name = await fetchKitchenChefName(shopId, kitchenId);
        if (!kitchenShiftEnabled.value || !matchesKitchenChef(name))
          throw new Error('当前无法开始制作，请确认负责人已开班');
      }
      if (!contextValid()) return;
      // 认领后再核对归属；提前开工只提交选中的一道菜。
      const currentQueue = await loadQueue(kitchenId);
      if (!contextValid()) return;
      const current = currentQueue.find((item) => item.orderOutId === task.orderOutId);
      if (
        !current ||
        current.kitchenStatus !== 0 ||
        current.operateStatus === 1 ||
        (configured.length ? String(current.stoveId) !== stoveId : Boolean(current.stoveId))
      )
        throw new Error('菜品归属或制作状态已变化，请刷新查看');
      submitted = true;
      const started = await changeKitchenStatus({ orderOutId: task.orderOutId, kitchenStatus: 5 });
      if (started.code !== 200) throw new Error(started.msg || '开始制作失败');
      if (!contextValid()) return;
      const verified = await loadQueue(kitchenId);
      if (!contextValid()) return;
      const result = verified.find((item) => item.orderOutId === task.orderOutId);
      if (result?.kitchenStatus !== 5 || (configured.length && String(result.stoveId) !== stoveId))
        throw new Error('暂未确认开工结果');
      showToast('已开始制作');
    } catch (error) {
      console.error('提前开始预约菜品失败', error);
      failureMessage = error instanceof Error ? error.message : '操作失败，请重试';
      if (submitted) failureMessage = `操作已提交，请核对负责厨师和制作状态：${failureMessage}`;
      showToast(failureMessage);
    } finally {
      if (contextValid()) {
        await refreshData();
        if (submitted && failureMessage) errorMessage.value = failureMessage;
      }
      busyTaskId.value = null;
    }
  };

  const changeTaskStatus = async (
    task: FoodOutDetailsListModel,
    kitchenStatus: 5 | 9 | 10,
  ): Promise<void> => {
    if (taskActionsDisabled.value || (task.operateStatus === 1 && kitchenStatus === 5)) return;
    const currentTask = tasks.value.find((item) => item.orderOutId === task.orderOutId);
    if (!currentTask || currentTask.kitchenStatus === kitchenStatus) return;
    invalidateRefresh();
    const version = requestVersion;
    const stoveId = selectedStoveId.value;
    busyTaskId.value = task.orderOutId;
    let statusSubmitted = false;
    try {
      const kitchenId = Number(currentKitchen.value);
      const stoveResponse = await fetchConfiguredStoves(kitchenId);
      if (stoveResponse.code !== 200)
        throw new Error(stoveResponse.msg || '暂时无法确认厨师是否接单，请刷新重试');
      const configuredStoves = (stoveResponse.result?.list || []).filter(
        (stove) => Number(stove.kitchenId) === kitchenId,
      );
      if (configuredStoves.length) {
        if (
          !configuredStoves.some(
            (stove) => stove.stoveId === selectedStove.value?.stoveId && stove.stoveStatus === 0,
          )
        )
          throw new Error('当前已停止接单，请刷新后点击“上班启用”');
      } else {
        const kitchenContactName = await fetchKitchenChefName(Number(currentShop.value), kitchenId);
        if (!kitchenShiftEnabled.value) throw new Error('当前未开班，请点击“上班启用”');
        if (!matchesKitchenChef(kitchenContactName))
          throw new Error('当前暂不能操作这些菜品，请刷新查看；如仍无法操作，请联系负责人');
      }
      let changedTaskIds = [task.orderOutId];
      let failedCount = 0;
      let batchErrorMessage = '';
      if (kitchenStatus === 5) {
        const [queue, settings] = await Promise.all([
          loadQueue(kitchenId),
          fetchKitchenScheduleSettings(kitchenId, Number(currentShop.value)),
        ]);
        if (version !== requestVersion || stoveId !== selectedStoveId.value) return;
        const latestTask = queue.find((item) => item.orderOutId === task.orderOutId);
        if (
          !latestTask ||
          (configuredStoves.length && Number(latestTask.stoveId || 0) !== Number(stoveId || 0)) ||
          latestTask.operateStatus === 1 ||
          ![0, 9].includes(latestTask.kitchenStatus)
        )
          throw new Error('菜品的制作进度或负责厨师已变化，请刷新查看');
        const result = await startKitchenTasksTogether(
          selectKitchenStartBatch(latestTask, queue, settings),
          () => version === requestVersion && stoveId === selectedStoveId.value,
        );
        changedTaskIds = result.startedTaskIds;
        failedCount = result.failedCount;
        batchErrorMessage = result.message;
        statusSubmitted = changedTaskIds.length > 0;
      } else {
        if (version !== requestVersion || stoveId !== selectedStoveId.value) return;
        const response = await changeKitchenStatus({ orderOutId: task.orderOutId, kitchenStatus });
        if (response.code !== 200) throw new Error(response.msg || '操作失败');
        statusSubmitted = true;
      }
      if (version !== requestVersion || stoveId !== selectedStoveId.value) return;
      await refreshData();
      if (errorMessage.value)
        throw new Error('操作已提交，暂时无法查看结果。请刷新确认，避免重复操作');
      if (
        changedTaskIds.some((id) => {
          const updatedTask = tasks.value.find((item) => item.orderOutId === id);
          return kitchenStatus === 10
            ? Boolean(updatedTask)
            : updatedTask?.kitchenStatus !== kitchenStatus;
        })
      ) {
        throw new Error('操作已提交，暂未确认操作结果。请刷新查看，避免重复操作');
      }
      if (failedCount)
        throw new Error(`有${failedCount}道菜未能开始制作：${batchErrorMessage}。请刷新核对后重试`);
      showToast(
        kitchenStatus === 10
          ? '制作完成'
          : kitchenStatus === 9
          ? '已暂停制作'
          : changedTaskIds.length > 1
          ? `已一起开始制作${changedTaskIds.length}道菜`
          : '已开始制作',
      );
    } catch (error) {
      console.error('更新厨房制作状态失败', error);
      const message = error instanceof Error ? error.message : '操作失败，请重试';
      if (statusSubmitted) errorMessage.value = message;
      showToast(message);
    } finally {
      busyTaskId.value = null;
    }
  };

  const closeTransfer = (): void => {
    if (transferSubmitting.value) return;
    transferTask.value = null;
    transferStoveId.value = '';
    busyTaskId.value = null;
  };

  const openTransfer = async (task: FoodOutDetailsListModel): Promise<void> => {
    if (taskActionsDisabled.value || !selectedStove.value) return;
    invalidateRefresh();
    const version = requestVersion;
    const kitchenId = Number(currentKitchen.value);
    busyTaskId.value = task.orderOutId;
    try {
      const [response, queue] = await Promise.all([
        fetchConfiguredStoves(kitchenId),
        loadQueue(kitchenId),
      ]);
      if (version !== requestVersion) return;
      if (response.code !== 200)
        throw new Error(response.msg || '暂时无法确认厨师是否接单，请刷新重试');
      stoves.value = response.result.list.filter((stove) => Number(stove.kitchenId) === kitchenId);
      tasks.value = queue;
      const currentTask = visibleTasks.value.find((item) => item.orderOutId === task.orderOutId);
      if (!currentTask || !selectedStoveEnabled.value)
        throw new Error('菜品的制作进度或厨师的接单情况已变化，请刷新查看');
      transferStoveId.value = '';
      transferTask.value = currentTask;
    } catch (error) {
      console.error('读取转交信息失败', error);
      showToast(error instanceof Error ? error.message : '读取转交信息失败');
    } finally {
      if (!transferTask.value) busyTaskId.value = null;
    }
  };

  const confirmTransfer = async (): Promise<void> => {
    const task = transferTask.value;
    const targetId = Number(transferStoveId.value);
    if (!task || !targetId || transferSubmitting.value) return;
    const version = requestVersion;
    const sourceStoveId = selectedStoveId.value;
    transferSubmitting.value = true;
    let failureMessage = '';
    let mutationStarted = false;
    try {
      const kitchenId = Number(currentKitchen.value);
      const [response, queue] = await Promise.all([
        fetchConfiguredStoves(kitchenId),
        loadQueue(kitchenId),
      ]);
      if (version !== requestVersion || sourceStoveId !== selectedStoveId.value)
        throw new Error('当前厨房或厨师已变化，请重新选择菜品');
      if (response.code !== 200)
        throw new Error(response.msg || '暂时无法确认厨师是否接单，请刷新重试');
      stoves.value = response.result.list.filter((stove) => Number(stove.kitchenId) === kitchenId);
      tasks.value = queue;
      const currentTask = queue.find((item) => item.orderOutId === task.orderOutId);
      const target = transferTargets.value.find((stove) => stove.stoveId === targetId);
      if (!currentTask || String(currentTask.stoveId) !== selectedStoveId.value)
        throw new Error('菜品已完成、取消或转交，请刷新确认');
      if (!selectedStoveEnabled.value) throw new Error('当前已停止接单，请刷新查看');
      if (!target) throw new Error('这位厨师已停止接单，请选择其他厨师');
      // 先暂停制作，再直接指定接收厨师，避免退回未分配队列被主屏抢先分配。
      if (currentTask.kitchenStatus === 5) {
        const paused = await changeKitchenStatus({ orderOutId: task.orderOutId, kitchenStatus: 9 });
        if (paused.code !== 200) throw new Error(paused.msg || '暂停制作失败');
        mutationStarted = true;
      }
      const assigned = await distributeStove({ orderOutId: task.orderOutId, stoveId: targetId });
      if (assigned.code !== 200) throw new Error(assigned.msg || '转交失败');
      mutationStarted = true;
      const reset = await changeKitchenStatus({ orderOutId: task.orderOutId, kitchenStatus: 0 });
      if (reset.code !== 200)
        throw new Error(
          reset.msg || '已转交，暂未确认菜品是否可以开始制作。请刷新查看，避免重复转交',
        );
      const latest = await loadQueue(kitchenId);
      tasks.value = latest;
      const transferred = latest.find((item) => item.orderOutId === task.orderOutId);
      if (Number(transferred?.stoveId) !== targetId || transferred?.kitchenStatus !== 0)
        throw new Error('转交已提交，暂未确认转交结果。请刷新查看，避免重复转交');
      showToast(`已转交给${target.stoveName}`);
      transferTask.value = null;
      transferStoveId.value = '';
    } catch (error) {
      console.error('转交菜品失败', error);
      failureMessage = error instanceof Error ? error.message : '转交失败，请重试';
      if (mutationStarted)
        failureMessage = `暂未确认转交结果：${failureMessage}。请刷新查看由谁制作以及制作进度。`;
      showToast(failureMessage);
      if (mutationStarted) {
        transferTask.value = null;
        transferStoveId.value = '';
      }
    } finally {
      if (!transferTask.value) {
        await refreshData();
        if (failureMessage) errorMessage.value = failureMessage;
        busyTaskId.value = null;
      }
      transferSubmitting.value = false;
    }
  };

  const autoStartNext = async (): Promise<void> => {
    if (!canAutoStart.value) return;
    const version = requestVersion;
    const kitchenId = Number(currentKitchen.value);
    const stoveId = selectedStoveId.value;
    busyTaskId.value = nextAutoStartTask.value?.orderOutId || null;
    try {
      // 开工前重新读取，避免按旧队列开始已暂停、已转交或顺序已变化的菜品。
      const [stoveResponse, queue, settings] = await Promise.all([
        fetchConfiguredStoves(kitchenId),
        loadQueue(kitchenId),
        fetchKitchenScheduleSettings(kitchenId, Number(currentShop.value)),
      ]);
      if (version !== requestVersion || stoveId !== selectedStoveId.value) return;
      if (stoveResponse.code !== 200)
        throw new Error(stoveResponse.msg || '暂时无法确认厨师是否接单，请刷新重试');
      stoves.value = stoveResponse.result.list.filter(
        (stove) => Number(stove.kitchenId) === kitchenId,
      );
      scheduleSettings.value = settings;
      scheduleNow.value = Date.now();
      tasks.value = queue;
      if (stoves.value.length) {
        if (!selectedStoveEnabled.value) return;
      } else {
        const kitchenContactName = await fetchKitchenChefName(Number(currentShop.value), kitchenId);
        if (version !== requestVersion || stoveId !== selectedStoveId.value) return;
        kitchenChefName.value = kitchenContactName;
        if (!canOperateCurrentKitchen.value) return;
      }
      const task = nextAutoStartTask.value;
      if (!task) return;
      busyTaskId.value = task.orderOutId;
      const result = await startKitchenTasksTogether(
        selectKitchenStartBatch(task, queue, scheduleSettings.value),
        () => version === requestVersion && stoveId === selectedStoveId.value,
      );
      if (version !== requestVersion || stoveId !== selectedStoveId.value) return;
      await refreshData();
      if (errorMessage.value)
        throw new Error('已尝试自动开始制作，暂时无法查看结果。请刷新确认，避免重复操作');
      if (
        result.startedTaskIds.some(
          (id) => tasks.value.find((item) => item.orderOutId === id)?.kitchenStatus !== 5,
        )
      )
        throw new Error('已尝试自动开始制作，暂未确认操作结果。请刷新查看，避免重复操作');
      if (result.failedCount)
        throw new Error(
          `有${result.failedCount}道菜未能自动开始制作：${result.message}。请刷新核对后重试`,
        );
      showToast(
        result.startedTaskIds.length > 1
          ? `已一起自动开始：${task.foodName}（${result.startedTaskIds.length}道菜）`
          : `已自动开始：${task.foodName}`,
      );
    } catch (error) {
      console.error('自动开始制作失败', error);
      if (version === requestVersion || Number(currentKitchen.value) === kitchenId) {
        errorMessage.value = error instanceof Error ? error.message : '自动开始失败，请刷新重试';
        showToast(errorMessage.value);
      }
    } finally {
      busyTaskId.value = null;
    }
  };

  watch(
    [
      visibleTasks,
      currentShop,
      currentKitchen,
      selectedStoveId,
      refreshing,
      loading,
      kitchenContextReady,
    ],
    () => {
      if (!kitchenContextReady.value || refreshing.value || loading.value) return;
      const scope = JSON.stringify([
        currentShop.value,
        currentKitchen.value,
        stoves.value.length ? selectedStoveId.value : 'kitchen',
      ]);
      const nextPriorities = new Map(
        visibleTasks.value.map((task) => {
          const priority = effectivePriority(task);
          const alertLevel = priority >= 999 ? 2 : priority >= 666 ? 1 : 0;
          return [task.orderOutId, alertLevel];
        }),
      );
      const previousPriorities = lastVisibleTaskPriorities;
      const canNotify = soundScope === scope && previousPriorities !== null;
      const hasNewDish =
        canNotify && [...nextPriorities.keys()].some((id) => !previousPriorities.has(id));
      const hasPriorityAlert =
        canNotify &&
        [...nextPriorities].some(([id, level]) => {
          const previousLevel = previousPriorities.get(id);
          return previousLevel !== undefined && level > previousLevel;
        });
      soundScope = scope;
      lastVisibleTaskPriorities = nextPriorities;
      // 新菜品使用原提示音；已有菜品叫菜、加急升级使用独立提示音，各类同轮合并。
      if (hasNewDish) void playNewDishSound();
      if (hasPriorityAlert) void playKitchenSound(priorityAudio);
    },
    { flush: 'post' },
  );

  watch(canAutoStart, (ready) => {
    if (ready) void autoStartNext();
  });

  watch(kitchens, (items) => {
    if (items.length && !items.some((item) => item.id === currentKitchen.value)) {
      currentKitchen.value = items[0].id;
    }
  });
  watch(accessibleShops, (items) => {
    if (items.length && !items.some((shop) => String(shop.shopId) === currentShop.value)) {
      currentShop.value = String(items[0].shopId);
    }
  });
  watch(
    () => scheduleSettings.value.advanceMinutes,
    (minutes) => {
      // 后台同步只更新未编辑的输入，不覆盖正在输入或保存失败后待重试的数字。
      if (!scheduleInputFocused.value && !scheduleInputDirty.value && !scheduleSaving.value)
        scheduleMinutesInput.value = String(minutes);
    },
  );

  watch(
    () => scheduledTasks.value.length,
    (count) => {
      if (!count) scheduledExpanded.value = false;
    },
  );
  watch([currentShop, currentKitchen], () => {
    soundScope = '';
    scheduledExpanded.value = false;
    scheduleMinutesInput.value = '30';
    scheduleInputFocused.value = false;
    scheduleInputDirty.value = false;
    lastVisibleTaskPriorities = null;
    invalidateRefresh();
    loading.value = false;
    tasks.value = [];
    scheduleReady.value = false;
    scheduleSettings.value = defaultKitchenScheduleSettings();
    stoves.value = [];
    kitchenChefName.value = '';
    kitchenContextReady.value = false;
    kitchenShiftEnabled.value = false;
    restoreKitchenShift();
    selectedStoveId.value = '';
    restoreMasterPending = readStored('master') === 'true';
    void refreshData(true);
  });
  watch(selectedStoveId, (value) => {
    if (value) writeStored('stove', value);
  });

  onMounted(() => {
    try {
      const src = new URL('@/assets/audio/new_order.mp3', import.meta.url).href;
      newDishAudio = new Audio(src);
      newDishAudio.preload = 'auto';
      newDishAudio.volume = 1.0;
      const prioritySrc = new URL('@/assets/audio/kitchen_alert.wav', import.meta.url).href;
      priorityAudio = new Audio(prioritySrc);
      priorityAudio.preload = 'auto';
      priorityAudio.volume = 1.0;
    } catch (error) {
      console.warn('厨房提示音初始化失败', error);
      soundError.value = '提示音加载失败，请刷新页面重试。';
    }
    void loadShops();
    refreshTimer = setInterval(() => {
      if (
        !refreshing.value &&
        !scheduleSaving.value &&
        !stoveBusy.value &&
        busyTaskId.value === null
      )
        void refreshData();
    }, 15000);
    clockTimer = setInterval(() => (now.value = Date.now()), 1000);
  });
  onUnmounted(() => {
    requestVersion++;
    newDishAudio?.pause();
    newDishAudio = null;
    priorityAudio?.pause();
    priorityAudio = null;
    if (refreshTimer) clearInterval(refreshTimer);
    if (clockTimer) clearInterval(clockTimer);
  });
</script>

<style scoped>
  .chef-screen {
    /* 1440px 为基准，宽屏同步放大；Pad 和手机保持原有尺寸。 */
    --chef-unit: clamp(1px, calc(100vw / 1440), 2.6667px);
    --chef-blue: #245dce;
    --chef-border: #dce3eb;
    min-height: 100vh;
    background: #f3f5f8;
    color: #202b3a;
    text-align: left;
    font-size: calc(14 * var(--chef-unit));
    line-height: 1.45;
  }

  .chef-screen h1,
  .chef-screen h2,
  .chef-screen h3,
  .chef-screen p {
    margin: 0;
  }

  .chef-screen button,
  .chef-home-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: calc(6 * var(--chef-unit));
    flex-shrink: 0;
    border: calc(1 * var(--chef-unit)) solid transparent;
    cursor: pointer;
    white-space: nowrap;
    font-family: inherit;
    text-decoration: none;
    transition: background-color 0.15s;
  }

  .chef-screen button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  .chef-screen button:focus-visible,
  .chef-screen select:focus-visible,
  .chef-screen input:focus-visible,
  .chef-home-link:focus-visible {
    outline: calc(3 * var(--chef-unit)) solid #60a5fa;
    outline-offset: calc(3 * var(--chef-unit));
  }

  .chef-header {
    position: sticky;
    top: 0;
    z-index: 10;
    padding: calc(10 * var(--chef-unit)) calc(20 * var(--chef-unit)) calc(8 * var(--chef-unit));
    background: #202a38;
    color: #fff;
    box-shadow: 0 calc(1 * var(--chef-unit)) calc(4 * var(--chef-unit)) #1723341a;
  }

  .chef-header-main,
  .chef-identity,
  .chef-header-actions,
  .chef-system-status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(8 * var(--chef-unit)) calc(12 * var(--chef-unit));
  }

  .chef-header-main {
    justify-content: space-between;
  }

  .chef-identity {
    min-width: 0;
  }

  .chef-identity h1,
  .chef-identity > strong {
    font-size: calc(22 * var(--chef-unit));
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .chef-identity-divider {
    width: calc(1 * var(--chef-unit));
    height: calc(20 * var(--chef-unit));
    background: #64748b;
  }

  .chef-shift-status {
    padding: calc(3 * var(--chef-unit)) calc(8 * var(--chef-unit));
    border-radius: calc(4 * var(--chef-unit));
    background: #475569;
    color: #e2e8f0;
    font-size: calc(12 * var(--chef-unit));
    white-space: nowrap;
  }

  .chef-shift-status.is-enabled {
    background: #174f43;
    color: #a7f3d0;
  }

  .chef-header-actions {
    gap: calc(8 * var(--chef-unit));
  }

  .chef-header-button,
  .chef-home-link {
    min-height: calc(44 * var(--chef-unit));
    padding: calc(8 * var(--chef-unit)) calc(12 * var(--chef-unit));
    border-radius: calc(6 * var(--chef-unit));
    background: #344255;
    color: #fff;
    font-size: calc(14 * var(--chef-unit));
  }

  .chef-home-link {
    width: calc(44 * var(--chef-unit));
  }

  .chef-header-button:hover:not(:disabled),
  .chef-home-link:hover {
    background: #46566c;
  }

  .chef-header-button.chef-shift-button {
    border-color: #68788b;
    background: transparent;
  }

  .chef-system-status {
    margin-top: calc(4 * var(--chef-unit));
    color: #b6c3d3;
    font-size: calc(12 * var(--chef-unit));
  }

  .chef-system-status i {
    margin-right: calc(4 * var(--chef-unit));
  }

  .chef-master-status {
    padding-left: calc(12 * var(--chef-unit));
    border-left: calc(1 * var(--chef-unit)) solid #526176;
  }

  .chef-master-status.is-master {
    color: #93c5fd;
  }

  .chef-refresh-note {
    margin-left: auto;
  }

  .chef-settings {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(10 * var(--chef-unit)) calc(20 * var(--chef-unit));
    margin-top: calc(10 * var(--chef-unit));
    padding: calc(12 * var(--chef-unit)) 0 calc(4 * var(--chef-unit));
    border-top: calc(1 * var(--chef-unit)) solid #435166;
  }

  .chef-settings label {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(8 * var(--chef-unit));
  }

  .chef-settings select,
  .chef-settings input[type='number'] {
    min-height: calc(44 * var(--chef-unit));
    max-width: min(calc(310 * var(--chef-unit)), 100%);
    padding: calc(8 * var(--chef-unit)) calc(10 * var(--chef-unit));
    border: calc(1 * var(--chef-unit)) solid #cbd5e1;
    border-radius: calc(6 * var(--chef-unit));
    background: #fff;
    color: #202b3a;
    font-size: calc(14 * var(--chef-unit));
  }

  .chef-settings input[type='number'] {
    width: calc(100 * var(--chef-unit));
  }

  .chef-master-switch input {
    width: calc(20 * var(--chef-unit));
    height: calc(20 * var(--chef-unit));
    accent-color: var(--chef-blue);
  }

  .chef-settings-note {
    width: 100%;
    color: #b6c3d3;
    font-size: calc(12 * var(--chef-unit));
  }

  .chef-main {
    max-width: calc(1920 * var(--chef-unit));
    margin: 0 auto;
    padding: calc(16 * var(--chef-unit)) calc(20 * var(--chef-unit));
  }

  .chef-notice {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: calc(8 * var(--chef-unit));
    margin-bottom: calc(10 * var(--chef-unit));
    padding: calc(8 * var(--chef-unit)) calc(12 * var(--chef-unit));
    border: calc(1 * var(--chef-unit)) solid #f2d7a0;
    border-radius: calc(6 * var(--chef-unit));
    background: #fffaed;
    color: #865416;
    overflow-wrap: anywhere;
  }

  .chef-section + .chef-section {
    margin-top: calc(18 * var(--chef-unit));
  }

  .chef-section-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: calc(4 * var(--chef-unit)) calc(12 * var(--chef-unit));
    margin-bottom: calc(8 * var(--chef-unit));
  }

  .chef-scheduled-toggle {
    padding: 0;
    background: transparent;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
  }

  .chef-section-heading h2 {
    display: flex;
    align-items: center;
    gap: calc(8 * var(--chef-unit));
    font-size: calc(18 * var(--chef-unit));
    font-weight: 700;
  }

  .chef-cooking-heading {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    gap: calc(8 * var(--chef-unit)) calc(12 * var(--chef-unit));
  }

  .chef-cooking-heading h2 {
    color: #1e50aa;
  }

  .chef-cooking-heading > .chef-section-hint {
    text-align: right;
  }

  .chef-task-summary {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: calc(4 * var(--chef-unit)) calc(16 * var(--chef-unit));
    padding: calc(5 * var(--chef-unit)) calc(12 * var(--chef-unit));
    border: calc(1 * var(--chef-unit)) solid #d8e3f4;
    border-radius: calc(6 * var(--chef-unit));
    background: #fff;
    color: #576579;
    font-size: calc(13 * var(--chef-unit));
  }

  .chef-task-summary > span {
    white-space: nowrap;
  }

  .chef-task-summary strong {
    margin-left: calc(3 * var(--chef-unit));
    color: #202b3a;
    font-size: calc(18 * var(--chef-unit));
    font-variant-numeric: tabular-nums;
  }

  .chef-summary-cooking,
  .chef-summary-cooking strong {
    color: #1e50aa;
  }

  .chef-summary-paused,
  .chef-summary-paused strong {
    color: #996018;
  }

  @media (max-width: 1100px) {
    .chef-cooking-heading {
      grid-template-columns: minmax(0, 1fr) auto;
    }

    .chef-task-summary {
      grid-column: 1 / -1;
      grid-row: 2;
    }
  }

  .chef-count {
    min-width: calc(26 * var(--chef-unit));
    padding: calc(1 * var(--chef-unit)) calc(7 * var(--chef-unit));
    border-radius: calc(5 * var(--chef-unit));
    background: #e2e8f0;
    color: #475569;
    text-align: center;
    font-size: calc(14 * var(--chef-unit));
    font-variant-numeric: tabular-nums;
  }

  .chef-count-active {
    background: #e2edff;
    color: var(--chef-blue);
  }

  .chef-count-paused {
    background: #fff0d0;
    color: #965510;
  }

  .chef-section-hint {
    color: #64748b;
    font-size: calc(12 * var(--chef-unit));
  }

  .chef-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc(8 * var(--chef-unit));
    min-height: calc(48 * var(--chef-unit));
    padding: calc(12 * var(--chef-unit));
    border: calc(1 * var(--chef-unit)) dashed #cbd5e1;
    border-radius: calc(6 * var(--chef-unit));
    color: #64748b;
    font-size: calc(14 * var(--chef-unit));
  }

  .chef-cooking-list,
  .chef-paused-list {
    display: grid;
    gap: calc(8 * var(--chef-unit));
  }

  .chef-cooking-card {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: calc(16 * var(--chef-unit));
    padding: calc(12 * var(--chef-unit)) calc(14 * var(--chef-unit));
    border: calc(1 * var(--chef-unit)) solid #f1c996;
    border-radius: calc(4 * var(--chef-unit));
    background: #fff4e5;
  }

  .chef-cooking-card.is-overdue {
    grid-template-columns: minmax(0, 1fr) calc(146 * var(--chef-unit)) auto;
    border-color: #edb2aa;
    background: #fff3f1;
  }

  .chef-task-content {
    display: flex;
    align-items: center;
    gap: calc(12 * var(--chef-unit));
    min-width: 0;
  }

  .chef-screen .chef-food-image-button {
    padding: 0;
    border: 0;
    border-radius: calc(6 * var(--chef-unit));
    background: transparent;
    cursor: zoom-in;
  }

  .chef-food-image {
    display: block;
    width: calc(64 * var(--chef-unit));
    height: calc(64 * var(--chef-unit));
    flex-shrink: 0;
    border-radius: calc(6 * var(--chef-unit));
    object-fit: cover;
  }

  .chef-task-info,
  .chef-queue-food {
    min-width: 0;
  }

  .chef-task-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(4 * var(--chef-unit)) calc(10 * var(--chef-unit));
    color: #64748b;
    font-size: calc(13 * var(--chef-unit));
  }

  .chef-table,
  .chef-order-number {
    font-size: calc(18 * var(--chef-unit));
    font-weight: 700;
  }

  .chef-table {
    color: var(--chef-blue);
    overflow-wrap: anywhere;
  }

  .chef-food-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: calc(2 * var(--chef-unit)) calc(10 * var(--chef-unit));
    min-width: 0;
  }

  .chef-food-heading h3,
  .chef-quantity {
    font-size: calc(22 * var(--chef-unit));
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .chef-quantity {
    white-space: nowrap;
  }

  .chef-food-cook {
    color: #944e10;
    font-size: calc(14 * var(--chef-unit));
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  .chef-task-notes {
    margin-top: calc(4 * var(--chef-unit)) !important;
    padding: calc(3 * var(--chef-unit)) calc(7 * var(--chef-unit));
    border-radius: calc(4 * var(--chef-unit));
    background: #fff5df;
    color: #944e10;
    font-size: calc(14 * var(--chef-unit));
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  .chef-task-notes i {
    margin-right: calc(5 * var(--chef-unit));
  }

  .chef-priority,
  .chef-dine-type {
    display: inline-block;
    padding: calc(2 * var(--chef-unit)) calc(7 * var(--chef-unit));
    border-radius: calc(4 * var(--chef-unit));
    background: #eef2f6;
    color: #576579;
    font-size: calc(12 * var(--chef-unit));
    font-weight: 600;
    white-space: nowrap;
  }

  .chef-priority-urgent,
  .chef-priority-called {
    padding: calc(4 * var(--chef-unit)) calc(10 * var(--chef-unit));
    color: #fff;
    font-size: calc(16 * var(--chef-unit));
    font-weight: 700;
    line-height: 1.5;
  }

  .chef-priority-urgent {
    background: #c62828;
  }

  .chef-priority-called {
    background: #a94b08;
  }

  .chef-dine-type-highlight {
    display: inline-block;
    padding: calc(4 * var(--chef-unit)) calc(10 * var(--chef-unit));
    border-radius: calc(4 * var(--chef-unit));
    background: #6d28d9;
    color: #fff;
    font-size: calc(18 * var(--chef-unit));
    font-weight: 700;
    line-height: 1.5;
    white-space: nowrap;
  }

  .chef-appointment-time {
    color: #6d28d9;
    font-size: calc(14 * var(--chef-unit));
    font-weight: 700;
  }

  .chef-queue-tags .chef-appointment-time {
    flex-basis: 100%;
  }

  .chef-task-timing {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: calc(14 * var(--chef-unit));
    border-left: calc(1 * var(--chef-unit)) solid var(--chef-border);
    color: #64748b;
    font-size: calc(12 * var(--chef-unit));
  }

  .chef-task-timing > strong {
    color: var(--chef-blue);
    font-size: calc(24 * var(--chef-unit));
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
  }

  .chef-task-timing.is-overdue > strong {
    color: #bd251d;
  }

  .chef-overdue-label {
    margin-top: calc(3 * var(--chef-unit));
    color: #bd251d;
    font-weight: 700;
  }

  .chef-card-actions,
  .chef-pending-actions,
  .chef-paused-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: calc(8 * var(--chef-unit));
  }

  .chef-card-actions {
    grid-column: -2 / -1;
  }

  .chef-button {
    min-width: calc(68 * var(--chef-unit));
    min-height: calc(48 * var(--chef-unit));
    padding: calc(8 * var(--chef-unit)) calc(12 * var(--chef-unit));
    border-radius: calc(6 * var(--chef-unit));
    font-size: calc(16 * var(--chef-unit));
    font-weight: 700;
  }

  .chef-button-secondary {
    border-color: #d7e0ea !important;
    background: #f4f7fb;
    color: #52647c;
  }

  .chef-button-pause {
    border-color: #f2d6ac !important;
    background: #fff3df;
    color: #a35b12;
  }

  .chef-button-complete {
    min-width: calc(92 * var(--chef-unit));
    background: #168557;
    color: #fff;
  }

  .chef-button-start {
    background: var(--chef-blue);
    color: #fff;
  }

  .chef-button-complete:hover:not(:disabled) {
    background: #126c46;
  }

  .chef-button-start:hover:not(:disabled) {
    background: #1b4eaf;
  }

  .chef-button-secondary:hover:not(:disabled) {
    background: #e7edf5;
  }

  .chef-button-pause:hover:not(:disabled) {
    background: #ffebc6;
  }

  .chef-queue {
    border: calc(1 * var(--chef-unit)) solid var(--chef-border);
    border-radius: calc(8 * var(--chef-unit));
    background: #fff;
    overflow: hidden;
  }

  .chef-queue-heading,
  .chef-pending-row {
    display: grid;
    grid-template-columns:
      calc(32 * var(--chef-unit)) calc(78 * var(--chef-unit)) calc(84 * var(--chef-unit))
      minmax(0, 1fr) calc(120 * var(--chef-unit)) calc(144 * var(--chef-unit));
    align-items: center;
    gap: calc(12 * var(--chef-unit));
    padding: calc(8 * var(--chef-unit)) calc(14 * var(--chef-unit));
  }

  .chef-queue-heading {
    background: #eaf0f6;
    color: #64748b;
    font-size: calc(12 * var(--chef-unit));
  }

  .chef-align-right {
    text-align: right;
  }

  .chef-pending-row {
    min-height: calc(72 * var(--chef-unit));
  }

  .chef-pending-row + .chef-pending-row {
    border-top: calc(1 * var(--chef-unit)) solid #edf0f4;
  }

  .chef-queue-rank {
    width: calc(28 * var(--chef-unit));
    height: calc(28 * var(--chef-unit));
    border-radius: calc(5 * var(--chef-unit));
    background: #f1f4f8;
    color: #64748b;
    text-align: center;
    font-size: calc(16 * var(--chef-unit));
    line-height: calc(28 * var(--chef-unit));
    font-variant-numeric: tabular-nums;
  }

  .chef-queue-rank.is-next {
    background: #e2edff;
    color: var(--chef-blue);
  }

  .chef-queue-order {
    display: grid;
    color: #64748b;
    font-size: calc(12 * var(--chef-unit));
    font-variant-numeric: tabular-nums;
  }

  .chef-queue-order strong {
    color: #445267;
  }

  .chef-queue-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(4 * var(--chef-unit));
  }

  .chef-paused-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) calc(146 * var(--chef-unit)) calc(144 * var(--chef-unit));
    align-items: center;
    gap: calc(16 * var(--chef-unit));
    padding: calc(10 * var(--chef-unit)) calc(14 * var(--chef-unit));
    border: calc(1 * var(--chef-unit)) solid #ecdfc7;
    border-radius: calc(4 * var(--chef-unit));
    background: #fff;
  }

  .chef-pause-reason {
    display: grid;
    gap: calc(2 * var(--chef-unit));
    color: #996018;
    font-size: calc(14 * var(--chef-unit));
  }

  .chef-pause-reason span {
    color: #64748b;
    font-size: calc(12 * var(--chef-unit));
  }

  .chef-transfer-modal {
    --chef-unit: clamp(1px, calc(100vw / 1440), 2.6667px);
    --chef-transfer-width: min(calc(460 * var(--chef-unit)), calc(100vw - 32px));
    --van-dialog-font-size: calc(20 * var(--chef-unit));
    --van-dialog-header-line-height: calc(28 * var(--chef-unit));
    --van-dialog-header-padding-top: calc(18 * var(--chef-unit));
    --van-dialog-radius: calc(10 * var(--chef-unit));
    top: 50%;
    max-height: 90dvh;
    overflow-y: auto;
  }

  .chef-transfer-dialog {
    display: grid;
    gap: calc(14 * var(--chef-unit));
    padding: calc(16 * var(--chef-unit)) calc(20 * var(--chef-unit)) calc(20 * var(--chef-unit));
    color: #202b3a;
    text-align: left;
    font-size: calc(16 * var(--chef-unit));
    line-height: 1.45;
  }

  .chef-transfer-dialog p {
    margin: 0;
  }

  .chef-transfer-food {
    padding: calc(12 * var(--chef-unit));
    border-radius: calc(6 * var(--chef-unit));
    background: #f3f6fa;
    overflow-wrap: anywhere;
  }

  .chef-transfer-table {
    color: #245dce;
    font-size: calc(14 * var(--chef-unit));
    font-weight: 600;
  }

  .chef-transfer-food-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: calc(4 * var(--chef-unit)) calc(10 * var(--chef-unit));
    font-size: calc(18 * var(--chef-unit));
  }

  .chef-transfer-quantity {
    white-space: nowrap;
  }

  .chef-transfer-note {
    color: #64748b;
    font-size: calc(14 * var(--chef-unit));
  }

  .chef-transfer-cook {
    margin-left: calc(8 * var(--chef-unit));
    color: #944e10;
    font-size: calc(14 * var(--chef-unit));
  }

  .chef-transfer-dialog label {
    display: grid;
    min-width: 0;
    gap: calc(6 * var(--chef-unit));
  }

  .chef-transfer-dialog select {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    height: calc(48 * var(--chef-unit));
    padding: 0 calc(12 * var(--chef-unit));
    border: 1px solid #cfd7e2;
    border-radius: calc(6 * var(--chef-unit));
    background: #fff;
    color: #202b3a;
    font-size: calc(16 * var(--chef-unit));
  }

  .chef-transfer-dialog select:focus-visible {
    outline: 2px solid #60a5fa;
    outline-offset: 2px;
  }

  .chef-transfer-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: calc(12 * var(--chef-unit));
    padding-top: calc(4 * var(--chef-unit));
  }

  .chef-transfer-actions :deep(.van-button) {
    width: 100%;
    height: calc(48 * var(--chef-unit));
    border-radius: calc(6 * var(--chef-unit));
    font-size: calc(16 * var(--chef-unit));
  }

  @media (min-width: 1440px) {
    .chef-food-heading h3,
    .chef-quantity {
      font-size: calc(26 * var(--chef-unit));
    }
  }

  @media (max-width: 1000px) {
    .chef-cooking-card,
    .chef-cooking-card.is-overdue {
      grid-template-columns: minmax(0, 1fr) auto;
      gap: calc(8 * var(--chef-unit)) calc(12 * var(--chef-unit));
    }

    .chef-task-content {
      grid-column: 1 / -1;
    }

    .chef-task-timing {
      padding-left: 0;
      border-left: 0;
    }

    .chef-queue-heading,
    .chef-pending-row {
      grid-template-columns:
        calc(28 * var(--chef-unit)) calc(66 * var(--chef-unit)) calc(68 * var(--chef-unit))
        minmax(0, 1fr) calc(120 * var(--chef-unit)) calc(144 * var(--chef-unit));
      gap: calc(8 * var(--chef-unit));
      padding: calc(8 * var(--chef-unit)) calc(10 * var(--chef-unit));
    }

    .chef-food-heading h3,
    .chef-quantity {
      font-size: calc(20 * var(--chef-unit));
    }
  }

  @media (max-width: 680px) {
    .chef-cooking-heading {
      display: flex;
    }

    .chef-task-summary {
      flex-basis: 100%;
      order: 1;
    }

    .chef-header {
      position: static;
      padding: calc(10 * var(--chef-unit)) calc(12 * var(--chef-unit));
    }

    .chef-identity h1,
    .chef-identity > strong {
      font-size: calc(18 * var(--chef-unit));
    }

    .chef-main {
      padding: calc(12 * var(--chef-unit));
    }

    .chef-food-image {
      width: calc(52 * var(--chef-unit));
      height: calc(52 * var(--chef-unit));
    }

    .chef-queue-heading {
      display: none;
    }

    .chef-pending-row {
      grid-template-columns: calc(28 * var(--chef-unit)) calc(78 * var(--chef-unit)) minmax(0, 1fr);
    }

    .chef-queue-food {
      grid-column: 2 / -1;
    }

    .chef-queue-tags {
      grid-column: 2;
    }

    .chef-pending-actions {
      grid-column: 3;
    }

    .chef-paused-row {
      grid-template-columns: minmax(0, 1fr) auto;
      gap: calc(8 * var(--chef-unit));
    }

    .chef-paused-row .chef-task-info {
      grid-column: 1 / -1;
    }

    .chef-button {
      min-width: calc(60 * var(--chef-unit));
      padding: calc(8 * var(--chef-unit)) calc(10 * var(--chef-unit));
    }

    .chef-button-complete {
      min-width: calc(80 * var(--chef-unit));
    }
  }
</style>
